// src/SupportChat.js
import React, { useEffect, useRef, useState } from "react";
import {
  Account,
  Databases,
  ID,
  Permission,
  Query,
  Role,
  Functions,
} from "appwrite";
import client from "./appwriteConfig";
import "./css/Support.css";

const DATABASE_ID = process.env.REACT_APP_DATABASE_ID;
const MESSAGES_ID = process.env.REACT_APP_SUPPORT_MESSAGES_COLLECTION_ID;
const ADMIN_USER_ID = process.env.REACT_APP_SUPPORT_ADMIN_USER_ID;
const database = new Databases(client);
const account = new Account(client);
const functions = new Functions(client);
const AI_UPLOAD_FUNCTION_ID = process.env.REACT_APP_GROQ_AI_FUNCTION_ID;
const formatMessageTime = (dateValue) => {
  if (!dateValue) return "";

  return new Date(dateValue).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};
const addUniqueMessage = (currentMessages, newMessage) => {
  if (currentMessages.some((message) => message.$id === newMessage.$id)) {
    return currentMessages;
  }

  return [...currentMessages, newMessage].sort(
    (a, b) => new Date(a.$createdAt) - new Date(b.$createdAt),
  );
};
function parseExecutionResponse(execution) {
  const raw =
    execution.responseBody || execution.response || execution.stdout || "";

  try {
    return JSON.parse(raw);
  } catch {
    return {
      success: false,
      detail: raw || "Function did not return JSON.",
    };
  }
}

const isMusicQuestion = (text) => {
  const q = text.toLowerCase();

  return [
    "music",
    "edm",
    "mix",
    "mixing",
    "master",
    "mastering",
    "fl studio",
    "vocal",
    "bass",
    "kick",
    "drum",
    "snare",
    "sound design",
    "synth",
    "chord",
    "melody",
    "scale",
    "raga",
    "compressor",
    "eq",
    "reverb",
    "delay",
    "808",
    "beatmaker",
    "beat maker",
    "sequencer",
    "piano roll",
    "melody modal",
    "song player",
    "pattern",
    "tutorial",
    "hxabyte",
    "how to make",
    "beat",
  ].some((word) => q.includes(word));
};
function SupportChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assistantTyping, setAssistantTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const latestAssistantMessageRef = useRef(null);
  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadLatestTicket(currentUser);
    }
  }, [currentUser]);

  useEffect(() => {
    if (ticket?.ticketId) {
      loadMessages(ticket.ticketId);
    } else {
      setMessages([]);
    }
  }, [ticket?.ticketId]);

  useEffect(() => {
    if (!ticket?.$id) return undefined;

    const unsubscribeMessages = client.subscribe(
      `databases.${DATABASE_ID}.collections.${MESSAGES_ID}.documents`,
      (response) => {
        const realtimeMessage = response.payload;

        if (
          realtimeMessage.ticketId === ticket.ticketId &&
          response.events.some((event) => event.endsWith(".create"))
        ) {
          setMessages((previousMessages) =>
            addUniqueMessage(previousMessages, realtimeMessage),
          );
        }
      },
    );

    return () => {
      unsubscribeMessages();
    };
  }, [ticket?.ticketId]);

  useEffect(() => {
    const last = messages[messages.length - 1];

    if (!last) return;

    if (last.senderType === "admin") {
      latestAssistantMessageRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen && ticket?.$id && ticket.unreadUser) {
      database
        .updateDocument(
          DATABASE_ID,
          MESSAGES_ID,
          ticket.latestMessageDocumentId,
          {
            unreadUser: false,
          },
        )
        .catch((error) => {
          console.error("Could not mark admin reply as read:", error);
        });
    }
  }, [isOpen, ticket?.$id, ticket?.unreadUser]);
  const buildMessageData = ({
    ticketId,
    message,
    senderId,
    senderEmail,
    senderType,
    status,
    unreadAdmin,
    unreadUser,
    subject,
    pageUrl,
  }) => ({
    ticketId,

    ownerId: currentUser.$id,
    ownerEmail: currentUser.email,
    ownerName: currentUser.name || currentUser.email,

    subject: subject || ticket?.subject || message.slice(0, 150),

    pageUrl: pageUrl || ticket?.pageUrl || window.location.href,

    status,
    unreadAdmin,
    unreadUser,

    senderId,
    senderEmail,
    senderType,
    message,
  });
  const loadCurrentUser = async () => {
    try {
      const user = await account.get();
      setCurrentUser(user);
    } catch (error) {
      setCurrentUser(null);
    } finally {
      setLoading(false);
    }
  };

  const loadLatestTicket = async (user) => {
    try {
      const response = await database.listDocuments(DATABASE_ID, MESSAGES_ID, [
        Query.equal("ownerId", user.$id),
        Query.orderDesc("$updatedAt"),
        Query.limit(1),
      ]);
      const latest = response.documents[0];

      if (!latest) {
        setTicket(null);
        return;
      }

      setTicket({
        ...latest,
        latestMessageDocumentId: latest.$id,
      });
    } catch (error) {
      console.error("Could not load support ticket:", error);
    }
  };

  const loadMessages = async (ticketId) => {
    try {
      const response = await database.listDocuments(DATABASE_ID, MESSAGES_ID, [
        Query.equal("ticketId", ticketId),
        Query.orderAsc("$createdAt"),
        Query.limit(200),
      ]);

      setMessages(response.documents);
    } catch (error) {
      console.error("Could not load support messages:", error);
    }
  };

  const getTicketPermissions = (userId) => [
    Permission.read(Role.user(userId)),
    Permission.update(Role.user(userId)),

    Permission.read(Role.user(ADMIN_USER_ID)),
    Permission.update(Role.user(ADMIN_USER_ID)),
    Permission.delete(Role.user(ADMIN_USER_ID)),
  ];

  const getMessagePermissions = (userId) => [
    Permission.read(Role.user(userId)),
    Permission.update(Role.user(userId)),

    Permission.read(Role.user(ADMIN_USER_ID)),
    Permission.update(Role.user(ADMIN_USER_ID)),
    Permission.delete(Role.user(ADMIN_USER_ID)),
  ];

  const formatAIResponse = (text) => {
    if (!text) return "";

    return (
      text
        .replace(/\r\n/g, "\n")

        // Remove bullets placed on separate lines before text
        .replace(/•\s*\n\s*•/g, "• ")
        .replace(/\n\s*•\s*\n/g, "\n• ")

        // Fix numbered headings like:
        // 1.\n•\n•Title
        .replace(/(\d+)\.\s*\n+\s*•\s*\n*\s*•?/g, "$1. ")

        // Remove duplicate bullets
        .replace(/•\s*•/g, "• ")

        // Ensure numbered items start on a new line
        .replace(/\s*(\d+\.)\s+/g, "\n$1 ")

        // Ensure bullets start on new lines
        .replace(/\s*•\s+/g, "\n• ")

        // Clean multiple blank lines
        .replace(/\n{3,}/g, "\n\n")

        .trim()
    );
  };
  const sendMessage = async () => {
    const cleanedMessage = messageInput.trim();

    if (!cleanedMessage || sending || !currentUser) return;

    setSending(true);
    try {
      if (isMusicQuestion(cleanedMessage)) {
        const userTempMessage = {
          $id: `user-${Date.now()}`,
          senderType: "user",
          message: cleanedMessage,
          $createdAt: new Date().toISOString(),
        };

        setMessages((prev) => addUniqueMessage(prev, userTempMessage));
        setMessageInput("");

        let aiAnswer = "";

        setAssistantTyping(true);

        try {
          aiAnswer = await askLearnMusicAIFromSupport(cleanedMessage);
        } catch (err) {
          aiAnswer =
            err.message || "Sorry, I could not get an AI response right now.";
        } finally {
          setAssistantTyping(false);
        }

        const aiTempMessage = {
          $id: `ai-${Date.now()}`,
          senderType: "admin",
          message: formatAIResponse(aiAnswer),
          $createdAt: new Date().toISOString(),
        };

        setMessages((prev) => addUniqueMessage(prev, aiTempMessage));

        return;
      }
      const ticketId = ticket?.ticketId || ID.unique();

      const createdMessage = await database.createDocument(
        DATABASE_ID,
        MESSAGES_ID,
        ID.unique(),
        buildMessageData({
          ticketId,
          message: cleanedMessage,

          senderId: currentUser.$id,
          senderEmail: currentUser.email,
          senderType: "user",

          status: "open",
          unreadAdmin: true,
          unreadUser: false,

          subject: ticket?.subject || cleanedMessage.slice(0, 150),

          pageUrl: ticket?.pageUrl || window.location.href,
        }),
        getMessagePermissions(currentUser.$id),
      );

      setMessages((previousMessages) =>
        addUniqueMessage(previousMessages, createdMessage),
      );

      setTicket({
        ...createdMessage,
        ticketId,
        latestMessageDocumentId: createdMessage.$id,
      });

      setMessageInput("");
    } catch (error) {
      console.error("Could not send support message:", error);
      alert(error.message || "Unable to send your message.");
    } finally {
      setSending(false);
    }
  };
  const askLearnMusicAIFromSupport = async (cleanedMessage) => {
    if (!AI_UPLOAD_FUNCTION_ID) {
      throw new Error("Missing REACT_APP_GROQ_AI_FUNCTION_ID in .env");
    }

    const execution = await functions.createExecution(
      AI_UPLOAD_FUNCTION_ID,
      JSON.stringify({
        question: cleanedMessage,
      }),
      false,
      "/ai/learn-music/chat",
      "POST",
      {
        "content-type": "application/json",
      },
    );
    console.log("AI execution:", execution);
    const data = parseExecutionResponse(execution);

    if (!data.success) {
      throw new Error(data.detail || data.error || "Learn Music AI failed.");
    }

    return data.answer;
  };
  const formatMessage = (text = "") => {
    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\n/g, "<br/>");
  };
  return (
    <>
      <button
        className="support-floating-button"
        onClick={() => setIsOpen((previousValue) => !previousValue)}
        aria-label="Open support chat"
      >
        {isOpen ? "×" : "💬"}

        {!isOpen && ticket?.unreadUser && (
          <span className="support-notification-dot" />
        )}
      </button>

      {isOpen && (
        <section className="support-chat-window">
          <header className="support-chat-header">
            <div>
              <strong>Hxabyte Assistant</strong>
              <span>
                Music AI • BeatMaker Help • Tutorials • Orders • Support
              </span>
            </div>

            <button onClick={() => setIsOpen(false)}>×</button>
          </header>

          <main className="support-chat-messages">
            {loading && <p className="support-information">Loading...</p>}

            {!loading && !currentUser && (
              <p className="support-information">
                Please log in to send a support query.
              </p>
            )}

            {currentUser && messages.length === 0 && (
              <p className="support-information">
                Hi {currentUser.name || "there"}! Ask us anything about your
                account, orders, beats, tutorials, or other services.
              </p>
            )}

            {messages.map((supportMessage) => (
              <div
                key={supportMessage.$id}
                ref={
                  supportMessage.senderType === "admin" &&
                  supportMessage.$id === messages[messages.length - 1]?.$id
                    ? latestAssistantMessageRef
                    : null
                }
                className={`support-message ${
                  supportMessage.senderType === "admin"
                    ? "support-message-admin"
                    : "support-message-user"
                }`}
              >
                <small className="support-message-meta-line">
                  <span>
                    {supportMessage.senderType === "admin" ? "Admin" : "You"}
                  </span>

                  <span className="support-message-time">
                    {formatMessageTime(supportMessage.$createdAt)}
                  </span>
                </small>

                <p
                  dangerouslySetInnerHTML={{
                    __html: formatMessage(supportMessage.message),
                  }}
                />
              </div>
            ))}
            {assistantTyping && (
              <div className="support-message support-message-admin support-typing-message">
                <small className="support-message-meta-line">
                  <span>Assistant</span>
                </small>

                <div className="support-typing-loader">
                  <span></span>
                  <span></span>
                  <span></span>
                  <em>Getting your answer</em>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </main>

          <footer className="support-chat-input">
            <textarea
              value={messageInput}
              placeholder={
                currentUser
                  ? "Type your question..."
                  : "Log in to contact support"
              }
              disabled={!currentUser || sending}
              onChange={(event) => setMessageInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage();
                }
              }}
            />

            <button
              onClick={sendMessage}
              disabled={!currentUser || sending || !messageInput.trim()}
            >
              {sending ? "..." : "Send"}
            </button>
          </footer>
        </section>
      )}
    </>
  );
}

export default SupportChat;
